import ColumnConfigurationIcon from '@/assets/icons/ColumnConfigurationIcon'
import DownloadIcon from '@/assets/icons/DownloadIcon'
import SearchIconBtn from '@/assets/icons/SearchIconBtn'
import CustomButton from '@/components/Returns/CustomButton'
import { ConfirmationDialog } from '@/components/common'
import Search from '@/components/common/Search'
import ColumnConfigurationBase from '@/components/common/columnConfiguration'
import { useSnackbar } from '@/contexts/SnackbarProvider'
import { MTCommonTable } from '@/multi-territory/mt-components/mt-shared/MTCommonTable'
import {
  deleteDuplicationRule,
  downloadDuplicationRules,
  fetchDuplicationRules,
  triggerDuplicationRuleDownload,
  type DuplicationRuleListRequest,
  type DuplicationRuleRow,
  type DuplicationRuleSearchFilter,
} from '@/multi-territory/mt-services/mt-duplication-rule-api'
import type { MTMetadataTableColumn } from '@/multi-territory/mt-types/mt-metadata'
import { formatMTHeaderLabel, formatMTSearchCriteria } from '@/multi-territory/mt-utils/mt-search-fields'
import { Box, IconButton, Tooltip, Typography } from '@mui/material'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useSelector } from 'react-redux'
import { MTDuplicationRuleForm } from './MTDuplicationRuleForm'

const DuplicationRuleSearch = Search as any
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const ColumnConfiguration = ColumnConfigurationBase as any

const TABLE_COLUMNS: MTMetadataTableColumn[] = [
  {
    id: 'ruleName',
    label: 'Rule name',
    type: 'text',
    editable: false,
    sortable: false,
    minWidth: '120px',
  },
  {
    id: 'active',
    label: 'Status',
    type: 'text',
    editable: false,
    sortable: false,
    minWidth: '100px',
    render: (value: any) => {
      const isActive = value === true || value === 'true' || value === 'Yes'
      return (
        <span
          style={{
            backgroundColor: isActive
              ? 'var(--bg-success)'
              : 'var(--table-header-bg)',
            color: 'var(--forbrand-primary)',
            padding: '4px 8px',
            borderRadius: 4,
            display: 'inline-block',
            fontSize: '12px',
            fontWeight: 500,
          }}
        >
          {isActive ? 'Active' : 'Inactive'}
        </span>
      )
    },
  },
  {
    id: 'parameter_1',
    label: 'Parameter 1',
    type: 'text',
    editable: false,
    sortable: false,
    minWidth: '120px',
  },
  {
    id: 'parameter_2',
    label: 'Parameter 2',
    type: 'text',
    editable: false,
    sortable: false,
    minWidth: '120px',
  },
  {
    id: 'parameter_3',
    label: 'Parameter 3',
    type: 'text',
    editable: false,
    sortable: false,
    minWidth: '120px',
  },
  {
    id: 'parameter_4',
    label: 'Parameter 4',
    type: 'text',
    editable: false,
    sortable: false,
    minWidth: '120px',
  },
  {
    id: 'parameter_5',
    label: 'Parameter 5',
    type: 'text',
    editable: false,
    sortable: false,
    minWidth: '120px',
  },
  {
    id: 'parameter_6',
    label: 'Parameter 6',
    type: 'text',
    editable: false,
    sortable: false,
    minWidth: '120px',
  },
  {
    id: 'parameter_7',
    label: 'Parameter 7',
    type: 'text',
    editable: false,
    sortable: false,
    minWidth: '120px',
  },
  {
    id: 'parameter_8',
    label: 'Parameter 8',
    type: 'text',
    editable: false,
    sortable: false,
    minWidth: '120px',
  },
  {
    id: 'createdBy',
    label: 'Created by',
    type: 'text',
    editable: false,
    sortable: false,
    minWidth: '140px',
  },
  {
    id: 'createdDate',
    label: 'Created at',
    type: 'text',
    editable: false,
    sortable: false,
    minWidth: '150px',
  },
  {
    id: 'lastModifiedBy',
    label: 'Updated by',
    type: 'text',
    editable: false,
    sortable: false,
    minWidth: '140px',
  },
  {
    id: 'lastModifiedDate',
    label: 'Updated at',
    type: 'text',
    editable: false,
    sortable: false,
    minWidth: '150px',
  },
  {
    id: '_action',
    label: 'Action',
    type: 'text',
    editable: false,
    sortable: false,
    minWidth: '90px',
    width: '90px',
  },
]

// Columns that must not appear as searchable fields (UI-only / action columns).
const NON_SEARCHABLE_COLUMN_IDS = new Set(['_action'])

// Table column ids that are dates (rendered with a date picker in the filter).
const DATE_COLUMN_IDS = new Set(['createdDate', 'lastModifiedDate'])

// The status column id ("active") is also the field name the backend search
// filters on (it is the boolean field returned in each row), so no id remap is
// needed here.
const SEARCH_FIELD_ID_OVERRIDES: Record<string, string> = {}

// Status is a fixed enumeration, so its filter is a dropdown (Active/Inactive)
// instead of a free-text input. Values match the ruleStatus strings the API
// returns/expects.
const STATUS_COLUMN_ID = 'active'
const STATUS_FILTER_OPTIONS = [
  { label: 'Active', value: 'true' },
  { label: 'Inactive', value: 'false' },
]

// Derive the search fields directly from TABLE_COLUMNS so EVERY filterable
// column (including Parameter 3-8 and any future columns) is available in the
// filter, instead of maintaining a separate hand-written list that drifts out
// of sync.
const SEARCH_FIELDS = TABLE_COLUMNS.filter(
  col => !NON_SEARCHABLE_COLUMN_IDS.has(col.id)
).map(col => {
  const id = SEARCH_FIELD_ID_OVERRIDES[col.id] ?? col.id
  const isDate = DATE_COLUMN_IDS.has(col.id)
  // Render the advance-search label exactly like the table header column
  // (sentence case / "running text", acronyms such as ID/TIN uppercase).
  const label = formatMTHeaderLabel(col.label)
  if (col.id === STATUS_COLUMN_ID) {
    return {
      id,
      label,
      type: 'dropdown',
      placeholder: `Select ${label}`,
      options: STATUS_FILTER_OPTIONS,
    }
  }
  return {
    id,
    label,
    type: isDate ? 'date' : 'text',
    placeholder: isDate ? 'DD/MM/YYYY' : `Enter ${label}`,
  }
})

/**
 * MTDuplicationRuleTable - Duplication Rule list table for multi-territory.
 * Loads data on viewTrigger from MTDefineKeyFilter.
 * APIs use dedicated duplication-rules endpoints with tanUuid path param and countryCode query param.
 */
export function MTDuplicationRuleTable() {
  const [data, setData] = useState<DuplicationRuleRow[]>([])
  const [loading, setLoading] = useState(false)
  const [showTable, setShowTable] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [itemsPerPage, setItemsPerPage] = useState(10)
  const [totalItems, setTotalItems] = useState(0)
  const [searchOpen, setSearchOpen] = useState(false)
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null)
  const searchIconRef = useRef<HTMLButtonElement>(null)
  const [searchFilters, setSearchFilters] = useState<
    DuplicationRuleSearchFilter[]
  >([])
  const [searchCriteria, setSearchCriteria] = useState<any[]>([
    {
      id: 1,
      logicalOperator: 'Where',
      field: 'ruleName',
      operator: 'EQUAL_TO',
      value: '',
    },
  ])

  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false)
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [deletingUuid, setDeletingUuid] = useState<string | null>(null)
  const [formOpen, setFormOpen] = useState(false)
  const [editingRule, setEditingRule] = useState<DuplicationRuleRow | null>(
    null
  )

  // Column configuration state (show/hide columns, same pattern as Rule Setting)
  const [columnConfigOpen, setColumnConfigOpen] = useState(false)
  const [columnConfigAnchor, setColumnConfigAnchor] =
    useState<HTMLElement | null>(null)
  const [hiddenColumns, setHiddenColumns] = useState<string[]>([])

  const { showSnackbar } = useSnackbar()

  // Redux selectors
  const countryCode = useSelector(
    (state: { country: { countryCode: string } }) => state.country.countryCode
  )
  const selectedEntityUuid = useSelector(
    (state: any) => state.mtEntityTan?.selectedEntityUuid ?? ''
  )
  const selectedTanUuid = useSelector(
    (state: any) => state.mtEntityTan?.selectedTanUuid ?? ''
  )
  const entityMetaByUuid = useSelector(
    (state: any) => state.mtEntityTan?.entityMetaByUuid ?? {}
  )
  const viewTrigger = useSelector(
    (state: any) => state.mtEntityTan?.viewTrigger ?? 0
  )
  const lastViewTriggerRef = useRef(viewTrigger)

  // Derive tanApplicable from entity metadata
  const selectedEntityMeta = entityMetaByUuid[selectedEntityUuid] || null
  const tanApplicable = selectedEntityMeta?.tanApplicable ?? true

  /**
   * API identifier logic:
   * - tanApplicable=true  -> use selectedTanUuid (tanMasterUuid)
   * - tanApplicable=false -> use selectedEntityUuid (entityMasterUuid)
   */
  const tanMasterUuid = tanApplicable ? selectedTanUuid : selectedEntityUuid

  // --- Load data ---
  const loadData = useCallback(
    async (filters?: DuplicationRuleSearchFilter[]) => {
      if (!countryCode || !tanMasterUuid) return
      setLoading(true)

      const payload: DuplicationRuleListRequest = {
        pageFilter: {
          currentPage,
          pageSize: itemsPerPage,
          sortField: 'createdDate',
          sortOrder: 'DESC',
        },
        searchCOList: filters ?? searchFilters,
        countryCode,
      }

      const result = await fetchDuplicationRules(tanMasterUuid, payload, {
        tanApplicable,
      })

      if (result.success) {
        const responseData = result.data as any
        const content: DuplicationRuleRow[] =
          responseData?.content ?? responseData?.data ?? []
        const total: number =
          responseData?.totalElements ?? responseData?.total ?? content.length
        setData(content)
        setTotalItems(total)
      } else {
        showSnackbar(
          result.error ?? 'Failed to load duplication rules',
          'error'
        )
        setData([])
        setTotalItems(0)
      }

      setLoading(false)
    },
    [
      countryCode,
      tanMasterUuid,
      currentPage,
      itemsPerPage,
      searchFilters,
      showSnackbar,
    ]
  )

  // Load data on viewTrigger
  useEffect(() => {
    if (viewTrigger > 0 && tanMasterUuid && countryCode) {
      if (viewTrigger <= lastViewTriggerRef.current) return
      lastViewTriggerRef.current = viewTrigger
      setShowTable(true)
      loadData()
    }
  }, [viewTrigger])

  // Reload on pagination change
  useEffect(() => {
    if (showTable && tanMasterUuid && countryCode) {
      loadData()
    }
  }, [currentPage, itemsPerPage])

  // --- Handlers ---
  const handleAdd = () => {
    // Only warn about inactivating an existing rule when there is actually an
    // ACTIVE rule. A list containing only inactive rules must not trigger the
    // "Active duplication rule found!" popup.
    const hasActiveRule = data.some(
      row =>
        (row.active as unknown) === true ||
        (row.active as unknown) === 'true' ||
        row.ruleStatus === 'Active' ||
        row.ruleStatus === 'ACTIVE'
    )
    if (hasActiveRule) {
      setConfirmDialogOpen(true)
    } else {
      setEditingRule(null)
      setFormOpen(true)
    }
  }

  const handleConfirmAdd = () => {
    setConfirmDialogOpen(false)
    setEditingRule(null)
    setFormOpen(true)
  }

  const handleEdit = (uuid: string) => {
    const row = data.find(r => r.uuid === uuid)
    if (row) {
      setEditingRule(row)
      setFormOpen(true)
    }
  }

  const handleFormClose = () => {
    setFormOpen(false)
    setEditingRule(null)
  }

  const handleFormSuccess = () => {
    setFormOpen(false)
    setEditingRule(null)
    loadData()
  }

  const handleDelete = (uuid: string) => {
    setDeletingUuid(uuid)
    setDeleteDialogOpen(true)
  }

  const handleConfirmDelete = async () => {
    if (!deletingUuid || !countryCode || !tanMasterUuid) return
    setDeleteDialogOpen(false)

    const result = await deleteDuplicationRule(countryCode, deletingUuid)
    if (result.success) {
      showSnackbar(result.data, 'success')
      loadData()
    } else {
      showSnackbar(result.error ?? 'Failed to delete rule', 'error')
    }
    setDeletingUuid(null)
  }

  const handlePageChange = (page: number) => setCurrentPage(page)
  const handleItemsPerPageChange = (size: number) => {
    setItemsPerPage(size)
    setCurrentPage(1)
  }

  const handleDownload = async () => {
    if (!countryCode || !tanMasterUuid) {
      showSnackbar('Please select entity and TAN first', 'error')
      return
    }
    if (totalItems === 0) {
      showSnackbar('No data found', 'info')
      return
    }
    showSnackbar('Download started', 'info')

    const payload: DuplicationRuleListRequest = {
      pageFilter: {
        currentPage: 1,
        pageSize: 10000,
        sortField: 'createdDate',
        sortOrder: 'DESC',
      },
      searchCOList: searchFilters,
      countryCode,
    }

    const result = await downloadDuplicationRules(
      countryCode,
      tanMasterUuid,
      payload,
      { tanApplicable }
    )
    if (result.success) {
      triggerDuplicationRuleDownload(
        result.data,
        `duplication-rules-export.xlsx`
      )
      showSnackbar('Download complete', 'success')
    } else {
      showSnackbar(result.error ?? 'Failed to download', 'error')
    }
  }

  const openSearchDialog = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget)
    setSearchOpen(true)
  }
  const closeSearchDialog = () => {
    setSearchOpen(false)
    setAnchorEl(null)
  }

  const applySearch = (searchList: any[]) => {
    // Parity with the Prepare page: run the popover output through
    // formatMTSearchCriteria (normalises date values) and send the FORMATTED
    // apiCriteria as searchCOList â€” this preserves the comparison operator
    // (EQUAL_TO/LIKE/...) and the AND/OR filterOperator. The mapped copy
    // (with the UI-only field/id/logicalOperator keys) is kept only for the
    // popover state.
    const apiCriteria = formatMTSearchCriteria(
      searchList
    ) as DuplicationRuleSearchFilter[]
    const mapped = apiCriteria.map((item: any, idx: number) => ({
      ...item,
      field: item.fieldName || item.field || '',
      id: idx + 1,
      logicalOperator: item.logicalOperator || (idx === 0 ? 'Where' : 'And'),
    }))
    setSearchCriteria(mapped)
    setSearchFilters(apiCriteria)
    setCurrentPage(1)
    setSearchOpen(false)
    loadData(apiCriteria)
  }

  const resetSearch = () => {
    setSearchCriteria([
      {
        id: 1,
        logicalOperator: 'Where',
        field: 'ruleName',
        operator: 'EQUAL_TO',
        value: '',
      },
    ])
    setSearchFilters([])
    setSearchOpen(false)
    setCurrentPage(1)
    loadData([])
  }

  // --- Column configuration ---
  // The `_action` column is always shown and cannot be toggled off.
  const configurableColumns = useMemo(
    () => TABLE_COLUMNS.filter(col => col.id !== '_action'),
    []
  )

  const visibleColumns = useMemo(
    () => TABLE_COLUMNS.filter(col => !hiddenColumns.includes(col.id)),
    [hiddenColumns]
  )

  const columnConfigColumns = useMemo(
    () => configurableColumns.map(col => ({ id: col.id, label: col.label })),
    [configurableColumns]
  )

  const columnConfigVisibleIds = useMemo(
    () =>
      configurableColumns
        .filter(col => !hiddenColumns.includes(col.id))
        .map(col => col.id),
    [configurableColumns, hiddenColumns]
  )

  const handleColumnConfigOpen = (event: React.MouseEvent<HTMLElement>) => {
    setColumnConfigAnchor(event.currentTarget)
    setColumnConfigOpen(prev => !prev)
  }

  const handleColumnConfigClose = () => {
    setColumnConfigOpen(false)
    setColumnConfigAnchor(null)
  }

  const handleColumnChange = (selectedColumns: string[]) => {
    const newHidden = configurableColumns
      .filter(col => !selectedColumns.includes(col.id))
      .map(col => col.id)
    setHiddenColumns(newHidden)
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
      {showTable && (
        <>
          {/* Header */}
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              width: '100%',
              my: '16px',
            }}
          >
            <Typography
              sx={{
                fontSize: '20px',
                fontWeight: 500,
                lineHeight: '130%',
                color: 'var(--text-heading)',
              }}
            >
              Duplication Rule
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
              <Tooltip title="Column Configuration">
                <IconButton
                  size="medium"
                  onClick={handleColumnConfigOpen}
                  aria-label="Column Configuration"
                >
                  <ColumnConfigurationIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Download Excel">
                <IconButton
                  size="medium"
                  onClick={handleDownload}
                  aria-label="Download"
                >
                  <DownloadIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="Search">
                <IconButton
                  size="medium"
                  ref={searchIconRef}
                  onClick={openSearchDialog}
                  aria-label="Search"
                >
                  <SearchIconBtn />
                </IconButton>
              </Tooltip>
              <Box
                sx={{
                  height: '16px',
                  borderLeft: '1px solid var(--border-subtel)',
                }}
              />
              <CustomButton onClick={handleAdd}>Add</CustomButton>
            </Box>
          </Box>

          {/* Table */}
          <MTCommonTable
            columns={visibleColumns}
            data={data}
            loading={loading}
            showCheckbox={false}
            showFooter
            onRowEdit={handleEdit}
            onRowDelete={handleDelete}
            isEditDisabled={row => row.active !== true}
            totalItems={totalItems}
            currentPage={currentPage}
            itemsPerPage={itemsPerPage}
            onPageChange={handlePageChange}
            onItemsPerPageChange={handleItemsPerPageChange}
            emptyMessage="No Rule Found"
          />

          {/* Search Popover */}
          <DuplicationRuleSearch
            open={searchOpen}
            anchorEl={anchorEl}
            fields={SEARCH_FIELDS as any}
            searchCriteria={searchCriteria}
            setSearchCriteria={setSearchCriteria}
            onClose={closeSearchDialog}
            onApply={applySearch}
            onReset={resetSearch}
            placement="bottom-start"
          />

          {/* Column Configuration */}
          <ColumnConfiguration
            columns={columnConfigColumns}
            visibleColumns={columnConfigVisibleIds}
            anchorEl={columnConfigAnchor}
            open={columnConfigOpen}
            onColumnChange={handleColumnChange}
            onClose={handleColumnConfigClose}
          />
        </>
      )}

      {/* Confirmation Dialog for Add */}
      <ConfirmationDialog
        open={confirmDialogOpen}
        onClose={() => setConfirmDialogOpen(false)}
        onConfirm={handleConfirmAdd}
        title="Active duplication rule found!"
        message='On creating a new duplication rule, existing rule will get "Inactive". Do you want to continue?'
        confirmText="Proceed"
        type="warning"
        showCancelButton={true}
        onCancel={() => setConfirmDialogOpen(false)}
      />

      {/* Add/Edit Form Dialog */}
      <MTDuplicationRuleForm
        open={formOpen}
        onClose={handleFormClose}
        onSuccess={handleFormSuccess}
        editingRule={editingRule}
        countryCode={countryCode}
        tanMasterUuid={tanMasterUuid}
        tanApplicable={tanApplicable}
      />

      {/* Confirmation Dialog for Delete */}
      <ConfirmationDialog
        open={deleteDialogOpen}
        onClose={() => setDeleteDialogOpen(false)}
        onConfirm={handleConfirmDelete}
        title="Delete Duplication Rule"
        message="Are you sure you want to delete this duplication rule? This action cannot be undone."
        confirmText="Delete"
        type="warning"
        showCancelButton={true}
        onCancel={() => {
          setDeleteDialogOpen(false)
          setDeletingUuid(null)
        }}
      />
    </Box>
  )
}
